import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import ReactQuill from 'react-quill';
import PropTypes from 'prop-types';
import 'react-quill/dist/quill.snow.css';

import { Wrapper, HelperText } from './styles';
import { applyLinkFormatting } from './util';
import texts from './texts.json';

const LinkHighlightInput = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  height,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const quillRef = useRef(null);
  const lastValueRef = useRef(value);
  const rafIdRef = useRef(null);

  const scheduleApplyLinks = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (!quillRef.current) return;
      const editor = quillRef.current.getEditor();

      const selection = editor.getSelection();
      const cursorIndex = selection ? selection.index : null;

      applyLinkFormatting(editor);

      if (cursorIndex != null && editor.hasFocus && editor.hasFocus()) {
        const newLength = editor.getLength();
        const adjustedIndex = Math.min(cursorIndex, Math.max(0, newLength - 1));
        editor.setSelection(adjustedIndex, 0, 'silent');
      }
    });
  }, []);

  // Sync Quill content when parent value changes (value is plain text)
  useEffect(() => {
    if (!quillRef.current) return;
    if (value === lastValueRef.current) return;

    const editor = quillRef.current.getEditor();
    const selection = editor.getSelection();
    const cursorIndex = selection ? selection.index : null;

    editor.setText(value || '', 'silent');
    applyLinkFormatting(editor);

    if (cursorIndex != null && editor.hasFocus && editor.hasFocus()) {
      const newLength = editor.getLength();
      const adjustedIndex = Math.min(cursorIndex, Math.max(0, newLength - 1));
      editor.setSelection(adjustedIndex, 0, 'silent');
    }

    lastValueRef.current = value;
  }, [value]);

  // Handle Ctrl+Click (or Cmd+Click on Mac) to open links
  useEffect(() => {
    if (!quillRef.current) {
      return undefined;
    }
    const editor = quillRef.current.getEditor();
    const editorElement = editor.root;

    const handleClick = (event) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) key is pressed
      const isModifierPressed = event.ctrlKey || event.metaKey;

      if (isModifierPressed) {
        // Check if the clicked element is a link
        const linkElement = event.target.closest('a');
        if (linkElement && linkElement.href) {
          // Open the link in a new tab
          window.open(linkElement.href, linkElement.target || '_blank', 'noopener,noreferrer');
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    // Add tooltips to links on hover
    const addTooltipsToLinks = () => {
      const links = editorElement.querySelectorAll('a');
      links.forEach((link) => {
        if (link.href && !link.getAttribute('title')) {
          link.setAttribute('title', texts.linkOpenTooltip);
        }
      });
    };

    // Add tooltips when links are added
    const observer = new MutationObserver(() => {
      addTooltipsToLinks();
    });

    // Initial tooltip addition
    addTooltipsToLinks();

    // Observe changes to the editor content
    observer.observe(editorElement, {
      childList: true,
      subtree: true,
    });

    editorElement.addEventListener('click', handleClick);

    return () => {
      editorElement.removeEventListener('click', handleClick);
      observer.disconnect();
    };
  }, []);

  // Handles the change event from the editor.
  const handleChange = (content, delta, source, editor) => {
    if (source === 'user') {
      const plainText = editor.getText().replace(/\n$/, '');

      // Prevent the next prop echo from resetting Quill + losing selection.
      lastValueRef.current = plainText;

      scheduleApplyLinks();
      onChange({ target: { value: plainText } });
    }
  };

  // Cleanup requestAnimationFrame on unmount
  useEffect(() => () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  }, []);

  return (
    <Wrapper $error={error} $focused={focused} $height={height}>
      <ReactQuill
        ref={quillRef}
        onChange={handleChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        modules={{
          toolbar: false,
          clipboard: {
            matchVisual: false,
          },
        }}
        formats={['link']}
        {...props}
      />
      {!!helperText && <HelperText $error={error}>{helperText}</HelperText>}
    </Wrapper>
  );
};

LinkHighlightInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  height: PropTypes.number,
};

LinkHighlightInput.defaultProps = {
  value: '',
  placeholder: '',
  error: false,
  helperText: '',
  height: 200,
};

export default LinkHighlightInput;
